var SHListItem_1;
import { __awaiter, __decorate } from 'tslib';
// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { html } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { customElement, property, query } from 'lit/decorators.js';
import { event, ShuiLitElement } from '../../utils/event-decorator.js';
import { ActiveChangedEvent } from '../../utils/events.js';
import listItemStyles from './sh-list-item.lit.scss.js';
/**
 * @fires active-changed {ActiveChangedEvent} - *hide emitted when the active property changes.
 */
let SHListItem = (SHListItem_1 = class SHListItem extends ShuiLitElement {
  constructor() {
    super(...arguments);
    /**Defines the selectable property of the list Item. Adds checkbox/ radio-button based on the `selection-type` of the list*/
    this.selectable = false;
    /** Defines the active state of the list Item.  If set to `true`, the style will adapt to represent that the item is currently active. */
    this.active = false;
    /** Defines the toggling of the active state of the list Item. */
    this.toggle = false;
    /** If set to true, the item will be shown as disabled and won't allow clicks. */
    this.disabled = false;
    /** Defines the divider style placed between the list item. Possible values are `inset-left`,`inset`,`full-width`,`none`.
     */
    this.divider = 'inset-left';
    /** Defines the  icon size of the list item icon. Possible values are `s`, `m`, `l`. */
    this.iconSize = 's';
    /** Defines the vertical alignment of the list Item's checkbox when `selectable` is set or arrow when `href` is set. Possible values are `center` and `top`. */
    this.verticalAlign = 'center';
    /** Defines the interactive state of the list Item. When set, list item hover styles are applied and it is clickable */
    this.clickable = false;
    /** Hides the arrow shown when `href` is set along with this property . */
    this.hideLinkArrow = false;
    /** ignore */
    this.role = 'listitem';
    // Store click handler reference for add/remove
    this.listItemClickHandler = () => {
      if (this.href && !this.disabled) {
        window.location.href = this.href;
      }
      this.handleClick();
    };
  }
  static get styles() {
    return [listItemStyles];
  }
  render() {
    var _a;
    return html`
      <div class="outer-wrapper">
        ${this.selectable
          ? html` <div class="select-wrapper">
              ${((_a = this.closest('sh-list')) === null || _a === void 0
                ? void 0
                : _a.selectionType) == 'single'
                ? html`<sh-radio-button
                    readonly
                    class="select"
                    label=""
                    .active=${this.active}
                  ></sh-radio-button>`
                : html`<sh-checkbox
                    readonly
                    class="select"
                    label=""
                    .active=${this.active}
                  ></sh-checkbox>`}
            </div>`
          : ''}
        <slot name="left" class="left-slot"></slot>
        <div class="content-wrapper">
          <div class="icon-text-wrapper">
            ${this.icon
              ? html`<sh-icon
                  class="icon"
                  size="${this.iconSize}"
                  color=${ifDefined(this.iconColor || undefined)}
                  icon="${this.icon}"
                ></sh-icon>`
              : ''}

            <div class="text-wrapper">
              ${this.label
                ? html`<sh-text class="label" size="title-1" color="primary"
                    >${this.label}</sh-text
                  >`
                : ''}
              ${this.info
                ? html`<sh-text class="info" size="body-2" color="secondary">${this.info}</sh-text>`
                : ''}
              <slot @slotchange="${this.updateAriaLabel}" class="slot-body"></slot>
            </div>

            <slot name="right" class="right"></slot>
            ${this.href && !this.hideLinkArrow
              ? html`<sh-icon class="arrow" button icon="arrow-right-s"></sh-icon>`
              : ''}
          </div>
          <slot name="bottom"></slot>
        </div>
        ${this.draggable
          ? html` <sh-icon class="draggable" icon="draggable-vertical-s" button></sh-icon>`
          : ''}
      </div>
      ${this.divider !== 'none' ? html` <div class="divider-wrapper"></div>` : ''}
    `;
  }
  connectedCallback() {
    const _super = Object.create(null, {
      connectedCallback: { get: () => super.connectedCallback },
    });
    return __awaiter(this, void 0, void 0, function* () {
      _super.connectedCallback.call(this);
      if (!this.id) {
        this.id = `list-item-${SHListItem_1.idCounter++}`;
      }
      yield this.updateComplete;
      this.updateRoleAndTabindex();
    });
  }
  // Helper to determine if the item is interactive
  get isInteractive() {
    return (this.selectable || this.clickable || this.toggle || this.href) && !this.disabled;
  }
  updateRoleAndTabindex() {
    const parentList = this.closest('sh-list');
    const parentRole =
      parentList === null || parentList === void 0 ? void 0 : parentList.getAttribute('role');
    // Always set role first
    if (this.href) {
      this.setAttribute('role', 'link');
    } else if (!this.selectable && !this.href && !this.toggle && !this.clickable) {
      this.setAttribute('role', parentRole === 'navigation' ? 'presentation' : 'listitem');
    } else if (this.toggle) {
      this.setAttribute('role', 'option');
      this.setAttribute('aria-selected', this.active ? 'true' : 'false');
    } else if (this.selectable || this.clickable) {
      this.setAttribute('role', 'listitem');
    }
    // Remove tabindex by default, only set for disabled
    this.removeAttribute('tabindex');
    // Disabled: always set aria-disabled and tabindex -1
    if (this.disabled) {
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute('tabindex', '-1');
    } else {
      this.removeAttribute('aria-disabled');
    }
  }
  firstUpdated() {
    // Attach click event only if interactive
    if (this.isInteractive) {
      this.addEventListener('click', this.listItemClickHandler);
    }
    this.updateAriaLabel();
  }
  update(changedProperties) {
    var _a;
    super.update(changedProperties);
    // Dynamically add/remove click event based on interactivity
    const wasInteractive =
      changedProperties.has('selectable') ||
      changedProperties.has('clickable') ||
      changedProperties.has('toggle') ||
      changedProperties.has('href') ||
      changedProperties.has('disabled');
    if (wasInteractive) {
      this.removeEventListener('click', this.listItemClickHandler);
      if (this.isInteractive) {
        this.addEventListener('click', this.listItemClickHandler);
      }
    }
    if (changedProperties.has('selectable') && this.selectable) {
      this.toggle = true;
    }
    // Always update role, tabindex, and aria-disabled on property changes
    if (
      changedProperties.has('toggle') ||
      changedProperties.has('clickable') ||
      changedProperties.has('disabled') ||
      changedProperties.has('selectable') ||
      changedProperties.has('href') ||
      changedProperties.has('active')
    ) {
      this.updateRoleAndTabindex();
    }
    if (changedProperties.has('active')) {
      if (this.toggle) {
        this.setAttribute('aria-selected', this.active ? 'true' : 'false');
        const shList = this.closest('sh-list');
        if (shList) {
          if (shList.selectionType == 'single' && this.active && !this.disabled) {
            shList.setAttribute('aria-activedescendant', this.id);
          } else {
            // Fix: Set aria-activedescendant to this.id if this item is being activated, otherwise fallback to first active item
            if (this.active && !this.disabled) {
              shList.setAttribute('aria-activedescendant', this.id);
            }
          }
        }
      }
      this.activeChangedEvent.emit(new ActiveChangedEvent({ bubbles: true, composed: true }));
    }
    if (changedProperties.has('label')) {
      // modify aria-label only if it was not set by the user
      if (this.ariaLabel === null || changedProperties.get('label') === this.ariaLabel) {
        this.ariaLabel = (_a = this.label) !== null && _a !== void 0 ? _a : null;
      }
    }
  }
  updateAriaLabel() {
    var _a, _b;
    // Only set aria-label if not set by user
    if (this.hasAttribute('aria-label')) {
      return;
    }
    const slot = this.slotBodyEl;
    const assignedNodes =
      (slot === null || slot === void 0 ? void 0 : slot.assignedNodes({ flatten: true })) || [];
    const textNode = assignedNodes.find((node) => {
      var _a;
      return (
        node.nodeType === Node.TEXT_NODE &&
        ((_a = node.textContent) === null || _a === void 0 ? void 0 : _a.trim())
      );
    });
    let ariaLabelTxt = '';
    if (textNode && !this.label) {
      ariaLabelTxt = textNode.textContent.trim();
    } else if (this.label) {
      ariaLabelTxt = this.label;
    }
    if (ariaLabelTxt) {
      this.ariaLabel = ariaLabelTxt;
      if (this.href) {
        // If href is set, also
        (_b =
          (_a = this.shadowRoot) === null || _a === void 0
            ? void 0
            : _a.querySelector('.arrow')) === null || _b === void 0
          ? void 0
          : _b.setAttribute('aria-label', ariaLabelTxt);
      }
    }
  }
  handleClick() {
    if (this.disabled) {
      return; // Do not toggle if the item is disabled
    }
    const shList = this.closest('sh-list');
    const isSingle =
      (shList === null || shList === void 0 ? void 0 : shList.selectionType) == 'single';
    if (this.toggle) {
      // Prevent toggling off the only active item in single selection
      if (isSingle && this.active) {
        // If this is the only active item, do not deactivate
        const activeItems =
          (shList === null || shList === void 0
            ? void 0
            : shList.querySelectorAll('sh-list-item[active]:not([disabled])')) || [];
        if (activeItems.length === 1 && activeItems[0] === this) {
          return;
        }
      }
      this.active = !this.active;
    }
    if (isSingle) {
      this.handleSingleSelection();
    }
  }
  handleSingleSelection() {
    var _a;
    const listItems =
      (_a = this.closest('sh-list')) === null || _a === void 0
        ? void 0
        : _a.querySelectorAll('sh-list-item[active]');
    listItems === null || listItems === void 0
      ? void 0
      : listItems.forEach((element) => {
          element.active = false;
        });
  }
});
SHListItem.idCounter = 0;
__decorate([query('slot.slot-body')], SHListItem.prototype, 'slotBodyEl', void 0);
__decorate([event()], SHListItem.prototype, 'activeChangedEvent', void 0);
__decorate([property({ type: String, reflect: true })], SHListItem.prototype, 'label', void 0);
__decorate([property({ type: String, reflect: true })], SHListItem.prototype, 'info', void 0);
__decorate([property({ type: String, reflect: true })], SHListItem.prototype, 'icon', void 0);
__decorate([property({ type: String, reflect: true })], SHListItem.prototype, 'href', void 0);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHListItem.prototype,
  'selectable',
  void 0
);
__decorate([property({ type: Boolean, reflect: true })], SHListItem.prototype, 'active', void 0);
__decorate([property({ type: Boolean, reflect: true })], SHListItem.prototype, 'toggle', void 0);
__decorate([property({ type: Boolean, reflect: true })], SHListItem.prototype, 'disabled', void 0);
__decorate([property({ type: String, reflect: true })], SHListItem.prototype, 'divider', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'icon-color' })],
  SHListItem.prototype,
  'iconColor',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'icon-size' })],
  SHListItem.prototype,
  'iconSize',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'vertical-align' })],
  SHListItem.prototype,
  'verticalAlign',
  void 0
);
__decorate([property({ type: Boolean, reflect: true })], SHListItem.prototype, 'clickable', void 0);
__decorate(
  [property({ type: Boolean, reflect: true, attribute: 'hide-link-arrow' })],
  SHListItem.prototype,
  'hideLinkArrow',
  void 0
);
__decorate([property({ type: String, reflect: true })], SHListItem.prototype, 'role', void 0);
SHListItem = SHListItem_1 = __decorate([customElement('sh-list-item')], SHListItem);
export { SHListItem };
